import type React from "react";
import {memo, useCallback, useEffect, useRef, useState} from "react";

import mergeRefs from "../../react/utils/mergeRefs";
import type {IPopoverProps} from "../ui/popover";
import {
    PopoverArrow,
    PopoverBackdrop,
    PopoverBody,
    PopoverContent,
    Popover as PopoverUI
} from "../ui/popover";
import type {PressableRef} from "../ui/pressable";
import {Pressable} from "../ui/pressable";
import {Text} from "../ui/text";

export type Props = {
    trigger: React.ReactNode;
    children: React.ReactNode;
    placement?: "top left" | "bottom";
    isOpen?: boolean;
    hasArrow?: boolean;
    offset?: number;
    size?: IPopoverProps["size"];
    contentHeight?: number;
    className?: string;
    onClose?: () => void;
};

const Popover: React.FC<Props> = ({
    trigger: triggerElement,
    isOpen,
    placement,
    children,
    hasArrow,
    size,
    contentHeight,
    className,
    onClose
}) => {
    const pressRef = useRef<PressableRef>(null);
    const [offset, setOffset] = useState(0);

    const updateOffset = useCallback(() => {
        const pressElement = pressRef.current;
        if (
            process.env.IS_WEB &&
            pressElement instanceof HTMLElement &&
            placement === "bottom" &&
            contentHeight
        ) {
            const rect = pressElement.getBoundingClientRect();
            const distanceToBottom = window.innerHeight - rect.bottom;
            const padding = 10;
            setOffset(Math.min(distanceToBottom - contentHeight - padding, 0));
        }
    }, [contentHeight, placement]);

    const trigger: IPopoverProps["trigger"] = useCallback(
        ({ref, ...triggerProps}) => {
            return (
                <Pressable
                    testID="popover-trigger"
                    {...triggerProps}
                    ref={mergeRefs(ref, pressRef)}
                >
                    {triggerElement}
                </Pressable>
            );
        },
        [triggerElement]
    );

    useEffect(() => {
        updateOffset();
    }, [updateOffset]);

    useEffect(() => {
        if (isOpen) {
            updateOffset();
        }
    }, [isOpen, updateOffset]);

    return (
        <PopoverUI
            isOpen={isOpen}
            offset={offset}
            placement={placement}
            size={size}
            trigger={trigger}
            onClose={onClose}
        >
            <PopoverBackdrop testID="popover-backdrop" />
            <PopoverContent className={className}>
                {hasArrow && <PopoverArrow />}
                <PopoverBody>
                    {typeof children === "string" ? (
                        <Text isParagraph color="white" textAlign="center" variant="xs">
                            {children}
                        </Text>
                    ) : (
                        children
                    )}
                </PopoverBody>
            </PopoverContent>
        </PopoverUI>
    );
};

export default memo(Popover);
